import { orderListByPriority } from '../../helpers/utility';
import { UUID, I18nMap } from '../../redux/app/basicModels';
import { Characteristic } from '../../redux/characteristic/models';
import { Indicator } from '../../redux/indicator/models';
import { OrderedInspectionGroup, OrderedPhenomenon } from '../../redux/methodology/models';

interface PhenomenonTreeNode {
  id?: UUID;
  company_id?: UUID | null;
  workspace_id?: UUID | null;
  name?: I18nMap;
  category_id?: UUID | null;
  short_description?: I18nMap;
  characteristics?: Characteristic[];
  indicators?: Indicator[];
}

type DragDropItem = (Characteristic | Indicator) & { isIndicator: boolean; priority?: number };

interface DragDropPhenomenon {
  id?: UUID;
  companyId?: UUID | null;
  workspaceId?: UUID | null;
  name?: I18nMap;
  categoryId?: UUID | null;
  items: DragDropItem[];
  shortDescription?: I18nMap;
  priority?: number;
}

interface DragDropLists {
  phenomenons: DragDropPhenomenon[];
  phenomenonsSelected: DragDropPhenomenon[];
}

interface PhenomenonScopeRef {
  id?: string;
  companyId?: string | null;
  workspaceId?: string | null;
}

export const hasPhenomenonOwner = (phenomenon: PhenomenonScopeRef): boolean =>
  Boolean(phenomenon.companyId) || Boolean(phenomenon.workspaceId);

export const getPhenomenonEditPath = (phenomenon: PhenomenonScopeRef, query?: string): string => {
  const suffix = query ? `?${query}` : '';
  if (phenomenon.workspaceId && !phenomenon.companyId) {
    return `/phenomenon-edit/${phenomenon.id}/by-workspace/${phenomenon.workspaceId}${suffix}`;
  }
  return `/phenomenon-edit/${phenomenon.id}/by-company/${phenomenon.companyId}${suffix}`;
};

// a little function to help us with reordering the result
export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 6;

export const getPhenomenonStyle = (isDragging, draggableStyle) => {
  return {
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    padding: grid * 2,
    margin: `0 0 ${grid}px 0`,
    textAlign: 'right',
    color: '#707374',
    fontSize: '14px',
    borderRadius: '4px',
    border: 'solid 1px #e8eaed',

    // change background colour if dragging
    background: isDragging ? '#edfef0' : '#f5f8fc',

    // styles we need to apply on draggables
    ...draggableStyle
  };
};

export const getItemStyle = (isDragging, draggableStyle) => {
  return {
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    padding: grid * 2,
    margin: `0 0 ${grid}px 0`,
    textAlign: 'right',
    borderRadius: '4px',
    border: 'solid 1px #e8eaed',
    color: '#2f3031',
    fontSize: '14px',

    // change background colour if dragging
    background: isDragging ? '#edfef0' : '#ffffff',

    // styles we need to apply on draggables
    ...draggableStyle
  };
};

export const getPhenomenonListStyle = isDraggingOver => ({
  background: isDraggingOver ? '#d3f6d9' : '#e8eaed',
  padding: 8,
  width: '100%',
  height: '100%'
});

export const getItemListStyle = isDraggingOver => ({
  background: isDraggingOver ? '#d3f6d9' : '#f5f8fc',
  padding: 4
  // width: 250
});

export const move: any = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone: any = Array.from(source);
  const destClone: any = Array.from(destination);

  const sourcePhenomenon = source[droppableSource.index];
  let hasPhenomenonDestList = false;
  destClone.forEach(phenomenon => {
    if (phenomenon.id === sourcePhenomenon.id) {
      sourcePhenomenon.items.forEach(item => {
        phenomenon.items.push(item);
      });
      hasPhenomenonDestList = true;
    }
  });

  const [removed] = sourceClone.splice(droppableSource.index, 1);

  if (!hasPhenomenonDestList) {
    destClone.splice(droppableDestination.index, 0, removed);
  }

  const result = {};
  result[droppableSource.droppableId] = JSON.parse(JSON.stringify(sourceClone));
  result[droppableDestination.droppableId] = JSON.parse(JSON.stringify(destClone));

  return result;
};

export const moveItem: any = (source, sourceType, destination, destinationType, item, phenomenonId) => {
  const sourceClone: any = Array.from(source);
  const destClone: any = Array.from(destination);

  let hasPhenomenonDestList = false;
  destClone.forEach(phenomenon => {
    if (phenomenon.id === phenomenonId) {
      phenomenon.items.push(item);
      hasPhenomenonDestList = true;
    }
  });

  sourceClone.forEach(phenomenon => {
    if (phenomenon.id === phenomenonId) {
      if (!hasPhenomenonDestList) {
        destClone.push({
          ...phenomenon,
          items: [item]
        });
      }
      const index = phenomenon.items.indexOf(item);
      phenomenon.items.splice(index, 1);
    }
  });

  destClone.forEach(function (phenomenon, index, object) {
    if (phenomenon.items.length === 0) {
      object.splice(index, 1);
    }
  });

  sourceClone.forEach(function (phenomenon, index, object) {
    if (phenomenon.items.length === 0) {
      object.splice(index, 1);
    }
  });

  const result = {};
  result[sourceType] = JSON.parse(JSON.stringify(sourceClone));
  result[destinationType] = JSON.parse(JSON.stringify(destClone));
  return result;
};

// Builds the "available" (phenomenons) and "inspection layout" (phenomenonsSelected) drag/drop lists
// from the phenomenon tree and the current inspection group. Pure function extracted from the
// PhenomenonGroup component so it can be unit-tested in isolation.
export const buildDragDropLists = (phenomenonsTree: PhenomenonTreeNode[], inspectionGroup: OrderedInspectionGroup): DragDropLists => {
  const orderedPhenomenons: OrderedPhenomenon[] = [];
  if (inspectionGroup?.ordered_categories) {
    inspectionGroup.ordered_categories.forEach(orderedCategory => {
      orderedCategory.ordered_phenomenons.forEach(orderedPhenomenon => {
        orderedPhenomenons.push(orderedPhenomenon);
      });
    });
  }
  // Index ordered phenomenons by component_id once (first match wins, like the previous
  // filter(...)[0]) so the per-phenomenon lookup below is O(1) instead of a full scan.
  const orderedPhenomenonByComponentId = new Map();
  orderedPhenomenons.forEach(orderedPhenomenon => {
    if (!orderedPhenomenonByComponentId.has(orderedPhenomenon.component_id)) {
      orderedPhenomenonByComponentId.set(orderedPhenomenon.component_id, orderedPhenomenon);
    }
  });

  let phenomenons: DragDropPhenomenon[] = [];
  let phenomenonsSelected: DragDropPhenomenon[] = [];
  phenomenonsTree.forEach(phenomenonTree => {
    const phenomenon: DragDropPhenomenon = {
      id: phenomenonTree.id,
      companyId: phenomenonTree.company_id,
      workspaceId: phenomenonTree.workspace_id,
      name: phenomenonTree.name,
      categoryId: phenomenonTree.category_id,
      items: [],
      shortDescription: phenomenonTree.short_description
    };
    const phenomenonSelected: DragDropPhenomenon = {
      id: phenomenonTree.id,
      companyId: phenomenonTree.company_id,
      workspaceId: phenomenonTree.workspace_id,
      name: phenomenonTree.name,
      categoryId: phenomenonTree.category_id,
      items: [],
      shortDescription: phenomenonTree.short_description
    };

    const items: DragDropItem[] = [];

    const defaultIndicators = new Set();

    if (phenomenonTree.characteristics)
      phenomenonTree.characteristics.forEach(characteristic => {
        if (characteristic.default_indicator_id) {
          defaultIndicators.add(characteristic.default_indicator_id);
        }

        items.push({
          ...characteristic,
          isIndicator: false
        });
      });

    if (phenomenonTree.indicators)
      phenomenonTree.indicators
        .filter(indicator => !defaultIndicators.has(indicator.id))
        .forEach(indicator => {
          items.push({
            ...indicator,
            isIndicator: true
          });
        });

    const orderedPhenomenonAvailable = orderedPhenomenonByComponentId.get(phenomenonTree.id);

    // Index this phenomenon's ordered components by component_id once (first match wins, like the
    // previous filter(...)[0]) so the per-item lookup below is O(1) instead of a full scan.
    const orderedComponentByComponentId = new Map();
    if (orderedPhenomenonAvailable) {
      orderedPhenomenonAvailable.ordered_components.forEach(orderedComponent => {
        if (!orderedComponentByComponentId.has(orderedComponent.component_id)) {
          orderedComponentByComponentId.set(orderedComponent.component_id, orderedComponent);
        }
      });
    }

    items.forEach(item => {
      const orderedComponents = orderedPhenomenonAvailable ? orderedComponentByComponentId.get(item.id) : undefined;
      const isSelected =
        orderedComponents && orderedComponents.class_name !== 'indicator' && orderedComponents.class_name !== 'characteristic';
      if (isSelected) {
        phenomenonSelected.items.push({ ...item, priority: orderedComponents.priority });
      } else {
        phenomenon.items.push(item);
      }
    });

    if (phenomenon.items.length > 0 || (phenomenonSelected.items.length === 0 && phenomenon.items.length === 0)) {
      phenomenons.push(phenomenon);
    }

    if (phenomenonSelected.items.length > 0) {
      phenomenonsSelected.push({
        ...phenomenonSelected,
        items: orderListByPriority(phenomenonSelected.items),
        priority: orderedPhenomenonAvailable.priority
      });
    }
  });

  if (inspectionGroup.phenomenons_selected_draft) {
    const draft = inspectionGroup.phenomenons_selected_draft;
    const phenomenonsCopy = phenomenons.slice();
    const phSelectedCopy = phenomenonsSelected.slice();
    draft.forEach(ph => {
      const index = phenomenonsCopy.findIndex(p => p.id === ph.id);
      if (index > -1) {
        const filteredItems = ph.items.map(item => phenomenonsCopy[index].items.find(_p => _p.id === item.id));

        if (filteredItems[0]) phenomenonsCopy.splice(index, 1);
      }
    });

    phSelectedCopy.forEach(ps => {
      const index = draft.findIndex(p => p.id === ps.id);
      if (index < 0) phenomenonsCopy.push(ps);
    });

    phenomenons = phenomenonsCopy;
    phenomenonsSelected = draft;
  }

  return {
    phenomenons,
    phenomenonsSelected: orderListByPriority(phenomenonsSelected)
  };
};
